import { useQuery } from '@tanstack/react-query'
import React, { Suspense } from 'react'
import { Actions } from '~/actions'
import { useTranslations } from '~/components/Locale'
import { ProductsDocument } from '~/gql'
import type { ListCollectionSection } from '~/types'
import {
	getPrice,
	isAccessory,
	isGiftCard,
	isSeed,
	productUrl,
	shopifyFetch,
	useDispatch,
	useFreeShippingThreshold
} from '~/utils'
import { ProductCard } from './ProductCard'
import { sortProducts } from './productUtils'
import { List, Section } from './Section'

export interface AllProductsProps {
	section: ListCollectionSection
}

export const AllProducts: React.FC<AllProductsProps> = ({ section }) => (
	<Suspense fallback={null}>
		<ProductList section={section} />
	</Suspense>
)

interface Props extends AllProductsProps {
	showPlaceholders?: boolean
}

const ProductList: React.FC<Props> = ({ section }) => {
	const dispatch = useDispatch()
	const t = useTranslations('react')
	const res = useQuery(['products'], async () => shopifyFetch(ProductsDocument, {})).data
	const freeShippingThreshold = useFreeShippingThreshold()

	const products = res?.data ?? []
	const other = products.filter(isGiftCard)
	const accessories = products.filter(isAccessory).sort(sortProducts(section.settings.sort))
	const seeds = products?.filter(isSeed)

	return (
		<>
			{res?.errors?.length ? (
				<div className="bg-red color-white p-4">
					{res.errors?.map((err, i) => (
						<p key={i}>{err.message}</p>
					))}
				</div>
			) : null}
			{accessories.length > 0 && (
				<Section title={t?.products.accessories} id="accessories">
					<p className="mb-20 text-center text-3xl font-semibold md:hidden">
						{t?.products.free_shipping_over} {getPrice(freeShippingThreshold, true)}
					</p>
					<List>
						{accessories.map(product => (
							<ProductCard.Container key={product.id} product={product}>
								<ProductCard.Actions>
									<ProductCard.Action href={productUrl(product)} className="px-12">
										{t?.actions.buy_now}
									</ProductCard.Action>
								</ProductCard.Actions>
							</ProductCard.Container>
						))}
					</List>
				</Section>
			)}
			{seeds.length > 0 && (
				<Section title={t?.products.seeds} id="fro" containerCns="bg-beige">
					<List className="grid-cols-2 gap-4 sm:gap-10">
						{products?.filter(isSeed).map(product => (
							<ProductCard.Container key={product.id} product={product} size="small">
								<ProductCard.Actions>
									<ProductCard.Action size="xs" href={productUrl(product)} className="sm:px-12">
										{t?.actions.buy_now}
									</ProductCard.Action>
									<ProductCard.Action
										size="xs"
										title={t?.actions.add_to_cart}
										onClick={_ =>
											dispatch(Actions.addToCart([{ quantity: 1, id: product.variants[0]?.id }]))
										}
									>
										+
									</ProductCard.Action>
								</ProductCard.Actions>
							</ProductCard.Container>
						))}
					</List>
				</Section>
			)}
			{other.length > 0 && (
				<Section title={t?.products.other} id="other">
					<List>
						{other.map(product => (
							<ProductCard.Container key={product.id} product={product}>
								<ProductCard.Actions>
									<ProductCard.Action href={productUrl(product)} className="px-12">
										{t?.actions.buy_now}
									</ProductCard.Action>
								</ProductCard.Actions>
							</ProductCard.Container>
						))}
					</List>
				</Section>
			)}
		</>
	)
}
